import { useState } from 'react';

import { Box, Checkbox, Typography } from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { validateEmail, validatePassword } from '@utils/validate';
import { signIn } from '@api/user';
import { store } from '@features/redux/store';
import { setIdToken, setPersist, setRefreshToken } from '@features/redux/slices/authSlice';
import { useAlert } from '@hooks/useAlert';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppRoutes, HELP_PORTAL_REDIRECT } from '@consts/routes';
import { setPartnerSession } from '@utils/partnerSession';
import { useSetLoading } from '@hooks/useSetLoading';
import { colors, typography } from '@components/design-system/tokens';
import { Button, TextField } from '@components/design-system';

export const LoginForm = () => {
    const { setAlert } = useAlert();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [keepSigned, setKeepSigned] = useState(false);
    const { setLoading } = useSetLoading();

    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');

    const updateEmail = (newEmail: string) => {
        const { message } = validateEmail(newEmail);
        setEmailError(message);
        setEmail(newEmail);
    };

    const updatePassword = (newPassword: string) => {
        const { message } = validatePassword(newPassword);
        setPasswordError(message);
        setPassword(newPassword);
    };

    const handleSignIn = async () => {
        setLoading(true);
        try {
            const { data } = await signIn(email, password);
            const { IdToken, RefreshToken } = data.AuthenticationResult || data;

            // Partner from the portal: mark the shared partner-session cookie and
            // stay on /login; LoginRoute hands off to the Help Portal once IdToken
            // is set. Everyone else → dashboard.
            const wantsHelpPortal = searchParams.get('redirect') === HELP_PORTAL_REDIRECT;
            if (wantsHelpPortal) setPartnerSession();

            store.dispatch(setRefreshToken(RefreshToken));
            store.dispatch(setIdToken(IdToken));
            store.dispatch(setPersist(keepSigned));

            if (!wantsHelpPortal) {
                navigate(AppRoutes.Insights);
            }
        } catch (err) {
            setAlert('Login failed! Check your credentials and try again!', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            component="form"
            onSubmit={(e) => {
                e.preventDefault();
                handleSignIn();
            }}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
                label="Email address"
                value={email}
                onChange={updateEmail}
                type="email"
                placeholder="Enter your email"
                error={Boolean(emailError)}
                errorMessage={emailError}
                startAdornment={<EmailOutlinedIcon sx={{ fontSize: 18, color: colors.neutral[400] }} />}
            />

            <TextField
                label="Password"
                value={password}
                onChange={updatePassword}
                type="password"
                placeholder="Enter your password"
                error={Boolean(passwordError)}
                errorMessage={passwordError}
                startAdornment={<LockOutlinedIcon sx={{ fontSize: 18, color: colors.neutral[400] }} />}
            />

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                }}
                onClick={() => setKeepSigned(!keepSigned)}>
                <Checkbox
                    checked={keepSigned}
                    onChange={(e) => setKeepSigned(e.target.checked)}
                    name="keepSignedIn"
                    sx={{
                        p: 0,
                        color: colors.neutral[300],
                        '&.Mui-checked': {
                            color: colors.primary[500],
                        },
                        '& .MuiSvgIcon-root': {
                            fontSize: 20,
                        },
                    }}
                />
                <Typography
                    sx={{
                        fontSize: typography.fontSize.sm,
                        color: colors.neutral[600],
                        userSelect: 'none',
                    }}>
                    Keep me signed in
                </Typography>
            </Box>

            <Button
                variant="primary"
                size="large"
                fullWidth
                disabled={Boolean(emailError) || Boolean(passwordError) || !email || !password}
                type="submit">
                Sign in
            </Button>
        </Box>
    );
};
